import { Close } from '@mui/icons-material'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { useGetDocumentComments } from '@/service-library/hooks/document-comments'
import Drawer from '@/components/drawer/Drawer'
import CommentComposer from './CommentComposer'
import CommentList from './CommentList'

type CommentsDrawerProps = {
  overlay: OverlayState
  documentId?: string
  /** The count badge's query key, owned by the parent
   * (ValidationOverlayContent). Threaded into the create/delete mutations so
   * they invalidate the exact query the badge reads from — keeping the count in
   * sync without a page reload. */
  countQueryKey?: QueryKey
}

export default function CommentsDrawer({
  overlay,
  documentId,
  countQueryKey,
}: CommentsDrawerProps) {
  // Fetched on demand: the query stays disabled until the panel is first
  // opened, so comments are never bundled into the initial document load.
  // Explicit ordering keeps the list chronological regardless of backend
  // defaults; id is a stable tiebreaker. created_at is excluded by the
  // serializer by default, so request it explicitly for the timestamps.
  // Author details are resolved from the auth endpoint (see useGetAuthUsers),
  // not embedded here.
  const {
    documentComments,
    isPending,
    queryKey: listQueryKey,
  } = useGetDocumentComments({
    filters: {
      document_id: documentId,
      limit: '1000',
      fields__include: 'created_at',
    },
    enabled: overlay.isOpen && !!documentId,
  })

  return (
    <Drawer overlay={overlay} anchor="right" size="lg">
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            minHeight: 52,
            px: 1.5,
            backgroundColor: 'background.paper',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" component="h2">
            <b>Comments</b>
          </Typography>
          <IconButton
            onClick={() => overlay.close()}
            aria-label="Close comments"
          >
            <Close />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <CommentList
            comments={documentComments}
            isLoading={isPending}
            listQueryKey={listQueryKey}
            countQueryKey={countQueryKey}
          />
        </Box>
        {documentId && (
          <CommentComposer
            documentId={documentId}
            listQueryKey={listQueryKey}
            countQueryKey={countQueryKey}
          />
        )}
      </Stack>
    </Drawer>
  )
}
